import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Input,
  CircularProgress,
  Button,
  Sheet,
  Select,
  Option,
} from '@mui/joy';
import { ChevronLeft, Ambulance, User, Clock3 } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { toast, ToastContainer } from 'react-toastify';
import { isSuperadmin } from '../../utils/permissions';
import 'react-toastify/dist/ReactToastify.css';

export default function DettaglioOrganizzazione() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const licenceLabel = (licence) => {
    switch (licence) {
      case 1:
        return 'SEM Free';
      case 2:
        return 'SEM Small';
      case 3:
        return 'SEM Medium';
      case 4:
        return 'SEM Large';
      default:
        return 'Non definita';
    }
  };

  const checklistCloseOptions = [1, 2, 3, 6, 12, 24, 48, 72];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await authFetch(`${process.env.REACT_APP_SERVER_HOST}/org/${id}/details`, {
          credentials: 'include',
        });

        if (!res.ok) throw new Error('Errore nel fetch dati ente');

        const data = await res.json();

        if (Array.isArray(data) && data.length > 0) {
          setOrgData({
            ...data[0],
            alert:
              data[0].alert !== null && data[0].alert !== undefined
                ? Number(data[0].alert)
                : 3,
          });
        } else {
          toast.error('Dati ente non disponibili');
        }
      } catch (err) {
        console.error('Errore fetch organizzazione:', err);
        toast.error('Errore nel caricamento dati ente');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (field, value) => {
    setOrgData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!orgData || !isSuperadmin()) return;

    setSaving(true);

    const payload = {
      alert: Number(orgData.alert) || 3,
    };

    ['partita_iva', 'indirizzo', 'email_mezzi', 'email_responsabile'].forEach((field) => {
      if (typeof orgData[field] === 'string' && orgData[field].trim()) {
        payload[field] = orgData[field].trim();
      }
    });

    try {
      const res = await authFetch(`${process.env.REACT_APP_SERVER_HOST}/org/${id}/details`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Errore nel salvataggio');

      toast.success('Modifiche salvate con successo', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Errore salvataggio organizzazione:', err);
      toast.error('Errore nel salvataggio', {
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography level="h3" mb={1}>
        Dettaglio Organizzazione
      </Typography>

      <Button
        startDecorator={<ChevronLeft />}
        onClick={() => navigate(-1)}
        variant="outlined"
        size="sm"
        sx={{ mb: 3 }}
      >
        Indietro
      </Button>

      {loading ? (
        <CircularProgress />
      ) : !orgData ? (
        <Typography color="danger">Organizzazione non trovata.</Typography>
      ) : (
        <>
          <Sheet
            variant="soft"
            color="neutral"
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 'md',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {orgData.img ? (
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 'md',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={orgData.img}
                    alt={`Logo ${orgData.name}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      p: 0.5,
                    }}
                  />
                </Box>
              ) : null}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Ambulance />
                <Typography level="title-md">{orgData.name}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Ambulance size={16} />
                <Typography level="body-sm">{orgData.vehicles} mezzi</Typography>
              </Box>

              <Typography
                level="body-xs"
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 'sm',
                  backgroundColor: orgData.status === 1 ? 'success.100' : 'danger.100',
                  color: orgData.status === 1 ? 'success.700' : 'danger.700',
                  fontWeight: 'md',
                }}
              >
                {orgData.status === 1 ? 'Attiva' : 'Disattivata'}
              </Typography>

              <Typography
                level="body-xs"
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 'sm',
                  backgroundColor: 'primary.100',
                  color: 'primary.700',
                  fontWeight: 'md',
                }}
              >
                Licenza: {licenceLabel(orgData.licence)}
              </Typography>
            </Box>
          </Sheet>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Sheet
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 'md',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minWidth: 280,
              }}
            >
              <Typography level="title-sm" mb={1}>
                Dati anagrafici
              </Typography>

              {!isSuperadmin() && (
                <Typography level="body-xs" color="neutral">
                  I dati anagrafici non sono modificabili. Contatta l’amministratore per aggiornamenti.
                </Typography>
              )}

              <Input
                value={orgData.partita_iva || ''}
                placeholder="Partita IVA"
                startDecorator={<Typography level="body-sm">Partita IVA</Typography>}
                onChange={(e) => handleChange('partita_iva', e.target.value)}
                disabled={!isSuperadmin()}
              />

              <Input
                value={orgData.indirizzo || ''}
                placeholder="Indirizzo"
                startDecorator={<Typography level="body-sm">Indirizzo</Typography>}
                onChange={(e) => handleChange('indirizzo', e.target.value)}
                disabled={!isSuperadmin()}
              />
            </Sheet>

            <Sheet
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 'md',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minWidth: 280,
              }}
            >
              <Typography level="title-sm" mb={1}>
                Email di notifica
              </Typography>

              <Input
                value={orgData.email_mezzi || ''}
                placeholder="Email mezzi"
                startDecorator={<Ambulance size={16} />}
                onChange={(e) => handleChange('email_mezzi', e.target.value)}
                disabled={!isSuperadmin()}
              />

              <Input
                value={orgData.email_responsabile || ''}
                placeholder="Email responsabile"
                startDecorator={<User size={16} />}
                onChange={(e) => handleChange('email_responsabile', e.target.value)}
                disabled={!isSuperadmin()}
              />
            </Sheet>
          </Box>

          <Sheet
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 'md',
              mt: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minWidth: 280,
            }}
          >
            <Typography level="title-sm" mb={1}>
              Impostazioni checklist
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography level="body-sm">
                Chiusura automatica checklist (ore)
              </Typography>

              <Select
                value={orgData.alert || 3}
                onChange={(_, value) => handleChange('alert', value)}
                disabled={!isSuperadmin()}
                startDecorator={<Clock3 size={16} />}
              >
                {checklistCloseOptions.map((hours) => (
                  <Option key={hours} value={hours}>
                    {hours} {hours === 1 ? 'ora' : 'ore'}
                  </Option>
                ))}
              </Select>

              <Typography level="body-xs" color="neutral">
                La checklist verrà chiusa automaticamente dopo il numero di ore selezionato.
              </Typography>
            </Box>
          </Sheet>

          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button
              size="sm"
              onClick={handleSave}
              loading={saving}
              disabled={saving || !isSuperadmin()}
              color="primary"
            >
              Salva
            </Button>
          </Box>

          <Typography level="body-xs" color="neutral" sx={{ mt: 2 }}>
            Creato il {new Date(orgData.created_at).toLocaleDateString()} - Aggiornato il{' '}
            {new Date(orgData.updated_at).toLocaleDateString()}
          </Typography>
        </>
      )}

      <ToastContainer />
    </Box>
  );
}